import { X } from "lucide-react";
import {
  EMPTY_PEOPLE_FILTERS,
  type PeopleFilterState,
} from "./people-filters";
import {
  EMAIL_STATUS_OPTIONS,
  EMPLOYEE_RANGES,
  SENIORITY_OPTIONS,
} from "./people-constants";

export type FilterChip = {
  id: string;
  label: string;
  clear: (prev: PeopleFilterState) => PeopleFilterState;
};

function listChip(
  id: string,
  prefix: string,
  values: string[],
  clearKey: keyof PeopleFilterState,
): FilterChip | null {
  if (!values.length) return null;
  return {
    id,
    label: `${prefix}: ${values.join(", ")}`,
    clear: (p) => ({ ...p, [clearKey]: [] }),
  };
}

export function buildFilterChips(value: PeopleFilterState): FilterChip[] {
  const chips: FilterChip[] = [];

  if (value.personaIcpId) {
    chips.push({
      id: "persona",
      label: `Persona: ${value.personaIcpId}`,
      clear: (p) => ({ ...p, personaIcpId: undefined }),
    });
  }
  for (const t of value.personTitles) {
    chips.push({
      id: `title-${t}`,
      label: `Title: ${t}`,
      clear: (p) => ({
        ...p,
        personTitles: p.personTitles.filter((x) => x !== t),
      }),
    });
  }
  if (!value.includeSimilarTitles) {
    chips.push({
      id: "sim-titles",
      label: "Similar titles: off",
      clear: (p) => ({ ...p, includeSimilarTitles: true }),
    });
  }
  for (const id of value.personSeniorities) {
    const opt = SENIORITY_OPTIONS.find((o) => o.id === id);
    chips.push({
      id: `sen-${id}`,
      label: opt?.label ?? id,
      clear: (p) => ({
        ...p,
        personSeniorities: p.personSeniorities.filter((x) => x !== id),
      }),
    });
  }
  for (const loc of value.personLocations) {
    chips.push({
      id: `loc-${loc}`,
      label: `Location: ${loc}`,
      clear: (p) => ({
        ...p,
        personLocations: p.personLocations.filter((x) => x !== loc),
      }),
    });
  }
  if (value.qOrganizationName.trim()) {
    chips.push({
      id: "company",
      label: `Company: ${value.qOrganizationName}`,
      clear: (p) => ({ ...p, qOrganizationName: "" }),
    });
  }
  for (const d of value.organizationDomains) {
    chips.push({
      id: `dom-${d}`,
      label: `Domain: ${d}`,
      clear: (p) => ({
        ...p,
        organizationDomains: p.organizationDomains.filter((x) => x !== d),
      }),
    });
  }
  for (const loc of value.organizationLocations) {
    chips.push({
      id: `hq-${loc}`,
      label: `HQ: ${loc}`,
      clear: (p) => ({
        ...p,
        organizationLocations: p.organizationLocations.filter((x) => x !== loc),
      }),
    });
  }
  for (const id of value.organizationIds) {
    chips.push({
      id: `org-${id}`,
      label: `Org: ${id}`,
      clear: (p) => ({
        ...p,
        organizationIds: p.organizationIds.filter((x) => x !== id),
      }),
    });
  }
  for (const id of value.organizationNumEmployeesRanges) {
    const opt = EMPLOYEE_RANGES.find((o) => o.id === id);
    chips.push({
      id: `emp-${id}`,
      label: `# Emp: ${opt?.label ?? id}`,
      clear: (p) => ({
        ...p,
        organizationNumEmployeesRanges:
          p.organizationNumEmployeesRanges.filter((x) => x !== id),
      }),
    });
  }
  for (const id of value.contactEmailStatus) {
    const opt = EMAIL_STATUS_OPTIONS.find((o) => o.id === id);
    chips.push({
      id: `email-${id}`,
      label: `Email: ${opt?.label ?? id}`,
      clear: (p) => ({
        ...p,
        contactEmailStatus: p.contactEmailStatus.filter((x) => x !== id),
      }),
    });
  }
  for (const ind of value.industries) {
    chips.push({
      id: `ind-${ind}`,
      label: `Industry: ${ind}`,
      clear: (p) => ({
        ...p,
        industries: p.industries.filter((x) => x !== ind),
      }),
    });
  }
  if (value.qKeywords.trim()) {
    chips.push({
      id: "kw",
      label: `Keywords: ${value.qKeywords}`,
      clear: (p) => ({ ...p, qKeywords: "" }),
    });
  }
  if (value.revenueMin != null || value.revenueMax != null) {
    chips.push({
      id: "rev",
      label: `Revenue: ${value.revenueMin ?? "…"}–${value.revenueMax ?? "…"}`,
      clear: (p) => ({
        ...p,
        revenueMin: undefined,
        revenueMax: undefined,
      }),
    });
  }
  const techChip = listChip(
    "tech-any",
    "Tech any",
    value.currentlyUsingAnyOfTechnologyUids,
    "currentlyUsingAnyOfTechnologyUids",
  );
  if (techChip) chips.push(techChip);
  const techAll = listChip(
    "tech-all",
    "Tech all",
    value.currentlyUsingAllOfTechnologyUids,
    "currentlyUsingAllOfTechnologyUids",
  );
  if (techAll) chips.push(techAll);
  const techEx = listChip(
    "tech-ex",
    "Tech exclude",
    value.currentlyNotUsingAnyOfTechnologyUids,
    "currentlyNotUsingAnyOfTechnologyUids",
  );
  if (techEx) chips.push(techEx);

  for (const t of value.qOrganizationJobTitles) {
    chips.push({
      id: `job-${t}`,
      label: `Hiring: ${t}`,
      clear: (p) => ({
        ...p,
        qOrganizationJobTitles: p.qOrganizationJobTitles.filter((x) => x !== t),
      }),
    });
  }
  for (const loc of value.organizationJobLocations) {
    chips.push({
      id: `jobloc-${loc}`,
      label: `Job loc: ${loc}`,
      clear: (p) => ({
        ...p,
        organizationJobLocations: p.organizationJobLocations.filter(
          (x) => x !== loc,
        ),
      }),
    });
  }
  if (
    value.organizationNumJobsMin != null ||
    value.organizationNumJobsMax != null
  ) {
    chips.push({
      id: "jobs-n",
      label: `Open jobs: ${value.organizationNumJobsMin ?? "…"}–${value.organizationNumJobsMax ?? "…"}`,
      clear: (p) => ({
        ...p,
        organizationNumJobsMin: undefined,
        organizationNumJobsMax: undefined,
      }),
    });
  }
  if (
    value.organizationJobPostedAtMin ||
    value.organizationJobPostedAtMax
  ) {
    chips.push({
      id: "jobs-d",
      label: `Posted: ${value.organizationJobPostedAtMin || "…"} → ${value.organizationJobPostedAtMax || "…"}`,
      clear: (p) => ({
        ...p,
        organizationJobPostedAtMin: "",
        organizationJobPostedAtMax: "",
      }),
    });
  }
  for (const s of value.sources ?? []) {
    chips.push({
      id: `src-${s}`,
      label: `Source: ${s}`,
      clear: (p) => ({
        ...p,
        sources: (p.sources ?? []).filter((x) => x !== s),
      }),
    });
  }
  if (value.createdAfter || value.createdBefore) {
    chips.push({
      id: "created",
      label: `Created: ${value.createdAfter || "…"} → ${value.createdBefore || "…"}`,
      clear: (p) => ({ ...p, createdAfter: "", createdBefore: "" }),
    });
  }

  return chips;
}

export function PeopleFilterChips({
  value,
  onChange,
  onClear,
  personaLabel,
}: {
  value: PeopleFilterState;
  onChange: (next: PeopleFilterState) => void;
  /** Clear all filters and re-search (includes page search bar). */
  onClear?: () => void;
  /** Display name for personaIcpId when known */
  personaLabel?: string;
}) {
  const chips = buildFilterChips(value).map((chip) =>
    chip.id === "persona" && personaLabel
      ? { ...chip, label: `Persona: ${personaLabel}` }
      : chip,
  );
  if (chips.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      {chips.map((chip) => (
        <button
          key={chip.id}
          type="button"
          onClick={() => onChange(chip.clear(value))}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-muted/20 px-2 py-1 text-[11px] text-foreground hover:bg-muted/40"
        >
          <span className="max-w-[220px] truncate">{chip.label}</span>
          <X className="size-3 text-muted-foreground" />
        </button>
      ))}
      <button
        type="button"
        onClick={() =>
          onClear ? onClear() : onChange(EMPTY_PEOPLE_FILTERS)
        }
        className="text-[11px] text-muted-foreground hover:text-foreground"
      >
        Clear all
      </button>
    </div>
  );
}
